// ── ScheduleGrid — 월간 스케줄 (직원별 일별 근무) ──────────
// 일별 행 클릭 → TimelineEditor 모달
// 📋 일괄 입력 → 기간·요일·시간 선택해 한 번에 채움

import React, { useState, useMemo } from 'react';
import TimelineEditor from './TimelineEditor';

const KOR_DAYS = ['일', '월', '화', '수', '목', '금', '토'];

function daysInMonth(year, month) { return new Date(year, month, 0).getDate(); }
function ymd(y, m, d) { return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`; }
function timeToMin(t) {
  if (!t) return 0;
  const [h, m] = t.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
}
function minToTime(m) {
  m = Math.max(0, Math.min(24 * 60, m));
  const h = Math.floor(m / 60);
  const min = m % 60;
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
}

function summarizeShift(sh) {
  if (!sh || sh.isOff) return null;
  const blocks = Array.isArray(sh.blocks) && sh.blocks.length > 0
    ? sh.blocks
    : (sh.startTime && sh.endTime ? [{ type: 'work', start: sh.startTime, end: sh.endTime }] : []);
  if (!blocks.length) return null;
  const works = blocks.filter(b => b.type === 'work').sort((a, b) => timeToMin(a.start) - timeToMin(b.start));
  const breaks = blocks.filter(b => b.type === 'break');
  let workMin = 0, breakMin = 0;
  for (const b of works) {
    let s = timeToMin(b.start), e = timeToMin(b.end);
    if (e <= s) e += 24 * 60;
    workMin += e - s;
  }
  for (const b of breaks) {
    let s = timeToMin(b.start), e = timeToMin(b.end);
    if (e <= s) e += 24 * 60;
    breakMin += e - s;
  }
  if (breakMin === 0 && sh.breakMinutes > 0) breakMin = Number(sh.breakMinutes);
  const netMin = Math.max(0, workMin - breakMin);
  return { works, breaks, workMin, breakMin, netMin };
}

export default function ScheduleGrid({ employees = [], schedules = {}, onSchedulesChange }) {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const activeEmps = employees.filter(e => e.active !== false);
  const [empId, setEmpId] = useState(activeEmps[0]?.id || '');
  const [editing, setEditing] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);

  const days = daysInMonth(year, month);
  const monthKey = `${year}-${String(month).padStart(2, '0')}`;
  const monthData = schedules[monthKey] || {};

  const totalHours = useMemo(() => {
    if (!empId) return 0;
    let total = 0;
    for (let d = 1; d <= days; d++) {
      const s = summarizeShift(monthData?.[d]?.[empId]);
      if (s) total += s.netMin / 60;
    }
    return Math.round(total * 100) / 100;
  }, [monthData, empId, days]);

  const moveMonth = delta => {
    let m = month + delta, y = year;
    if (m > 12) { m = 1; y++; }
    if (m < 1) { m = 12; y--; }
    setYear(y); setMonth(m);
  };

  const saveShift = (day, shift) => {
    const next = { ...schedules };
    if (!next[monthKey]) next[monthKey] = {};
    if (!next[monthKey][day]) next[monthKey][day] = {};
    next[monthKey][day][empId] = shift;
    onSchedulesChange(next);
    setEditing(null);
  };

  const clearShift = day => {
    const next = { ...schedules };
    if (next[monthKey]?.[day]?.[empId]) delete next[monthKey][day][empId];
    onSchedulesChange(next);
    setEditing(null);
  };

  const clearMonth = () => {
    const empName = activeEmps.find(e => e.id === empId)?.name;
    if (!window.confirm(`${month}월 ${empName}의 모든 스케줄을 비우시겠습니까?`)) return;
    const next = { ...schedules };
    if (next[monthKey]) {
      for (const d of Object.keys(next[monthKey])) {
        if (next[monthKey][d]?.[empId]) delete next[monthKey][d][empId];
      }
    }
    onSchedulesChange(next);
  };

  // 일괄 입력 적용
  const applyBulk = ({ fromDay, toDay, weekdays, startTime, endTime, breakStart, breakEnd, overwrite }) => {
    const next = { ...schedules };
    if (!next[monthKey]) next[monthKey] = {};
    let touched = 0;
    for (let d = fromDay; d <= toDay; d++) {
      const dow = new Date(year, month - 1, d).getDay();
      if (!weekdays.includes(dow)) continue;
      if (!next[monthKey][d]) next[monthKey][d] = {};
      if (next[monthKey][d][empId] && !overwrite) continue;
      const blocks = [{ type: 'work', start: startTime, end: endTime }];
      if (breakStart && breakEnd) {
        blocks.push({ type: 'break', start: breakStart, end: breakEnd });
      }
      const breakMin = breakStart && breakEnd ? (timeToMin(breakEnd) - timeToMin(breakStart)) : 0;
      next[monthKey][d][empId] = {
        blocks, isOff: false, isHoliday: false,
        startTime, endTime, breakMinutes: breakMin,
      };
      touched++;
    }
    onSchedulesChange(next);
    setBulkOpen(false);
    if (touched === 0) {
      alert('적용된 날짜가 없습니다. 기간/요일/덮어쓰기 옵션 확인.');
    }
  };

  if (activeEmps.length === 0) {
    return (
      <div style={{ padding: '32px', textAlign: 'center', background: '#fff',
                    borderRadius: '10px', border: '1px solid #e5e7eb', color: '#9ca3af', fontSize: '13px' }}>
        먼저 <strong>직원 관리</strong>에서 직원을 등록하세요.
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap' }}>
        <button onClick={() => moveMonth(-1)}
          style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #e5e7eb',
                   background: '#fff', cursor: 'pointer', fontSize: '13px' }}>‹</button>
        <strong style={{ fontSize: '15px', minWidth: '90px', textAlign: 'center' }}>
          {year}년 {month}월
        </strong>
        <button onClick={() => moveMonth(1)}
          style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #e5e7eb',
                   background: '#fff', cursor: 'pointer', fontSize: '13px' }}>›</button>

        <select value={empId} onChange={e => setEmpId(e.target.value)}
          style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #e5e7eb',
                   fontSize: '13px', flex: 1, minWidth: '120px' }}>
          {activeEmps.map(e => (
            <option key={e.id} value={e.id}>{e.name} ({e.position || '직원'})</option>
          ))}
        </select>
      </div>

      {/* 일괄 입력 + 비우기 */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '10px' }}>
        <button onClick={() => setBulkOpen(true)}
          style={{ padding: '7px 14px', borderRadius: '6px',
                   border: '1.5px solid #FF6F0F', background: '#FAFAFA',
                   color: '#18181B', fontSize: '13px', fontWeight: '700', cursor: 'pointer' }}>
          📋 일괄 입력
        </button>
        <button onClick={clearMonth}
          style={{ padding: '7px 14px', borderRadius: '6px',
                   border: '1px solid #e5e7eb', background: '#fff',
                   color: '#6b7280', fontSize: '12px', cursor: 'pointer' }}>
          🗑️ 월 전체 비우기
        </button>
      </div>

      <div style={{ background: '#FAFAFA', border: '1px solid #F4F4F5', borderRadius: '8px',
                    padding: '10px 14px', marginBottom: '12px', fontSize: '13px', color: '#18181B' }}>
        <strong>{activeEmps.find(e => e.id === empId)?.name}</strong> · {month}월 실근무 <strong>{totalHours}시간</strong>
      </div>

      {/* 일자 목록 — 자체 스크롤 */}
      <div style={{ background: '#fff', borderRadius: '10px', overflow: 'hidden',
                    border: '1px solid #e5e7eb', maxHeight: '60vh', overflowY: 'auto' }}>
        {[...Array(days)].map((_, i) => {
          const day = i + 1;
          const date = new Date(year, month - 1, day);
          const dow = date.getDay();
          const isWeekend = dow === 0 || dow === 6;
          const sh = monthData?.[day]?.[empId];
          const summary = summarizeShift(sh);

          return (
            <div key={day} onClick={() => setEditing({ day, shift: sh || {} })}
              style={{
                padding: '10px 14px', cursor: 'pointer',
                borderBottom: i < days - 1 ? '1px solid #f3f4f6' : 'none',
                display: 'flex', alignItems: 'center', gap: '10px',
                background: isWeekend ? '#fafaf9' : '#fff',
              }}>
              <div style={{ minWidth: '64px', fontSize: '13px', fontWeight: '600',
                            color: dow === 0 ? '#52525B' : dow === 6 ? '#2563eb' : '#374151' }}>
                {String(day).padStart(2, '0')} ({KOR_DAYS[dow]})
              </div>
              <div style={{ flex: 1, fontSize: '13px', color: sh ? '#111' : '#9ca3af' }}>
                {!sh ? '— 클릭해서 시간 입력' :
                 sh.isOff ? <span style={{ color: '#9ca3af' }}>OFF</span> :
                 !summary ? <span style={{ color: '#9ca3af' }}>—</span> : (
                  <>
                    {summary.works.map((b, i) => (
                      <span key={i} style={{ marginRight: 8 }}>
                        <strong>{b.start}~{b.end}</strong>
                      </span>
                    ))}
                    {summary.breakMin > 0 && (
                      <span style={{ color: '#A1A1AA', fontSize: 11 }}>☕ {summary.breakMin}분</span>
                    )}
                    <span style={{ marginLeft: 8, color: '#18181B', fontWeight: 700 }}>
                      ({(summary.netMin / 60).toFixed(2)}h)
                    </span>
                    {sh.isHoliday && <span style={{ marginLeft: 6, color: '#52525B', fontSize: 11 }}>🏖️ 휴일</span>}
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {editing && (
        <TimelineEditor
          date={ymd(year, month, editing.day)}
          shift={editing.shift}
          onSave={s => saveShift(editing.day, s)}
          onClear={() => clearShift(editing.day)}
          onCancel={() => setEditing(null)}
        />
      )}

      {bulkOpen && (
        <BulkScheduleModal
          year={year} month={month} days={days}
          empName={activeEmps.find(e => e.id === empId)?.name}
          onApply={applyBulk}
          onCancel={() => setBulkOpen(false)}
        />
      )}
    </div>
  );
}

// ── 일괄 입력 모달 ──
function BulkScheduleModal({ year, month, days, empName, onApply, onCancel }) {
  const [fromDay, setFromDay] = useState(1);
  const [toDay, setToDay] = useState(days);
  const [weekdays, setWeekdays] = useState([1, 2, 3, 4, 5]); // 월~금 기본
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('18:00');
  const [hasBreak, setHasBreak] = useState(true);
  const [breakStart, setBreakStart] = useState('12:00');
  const [breakEnd, setBreakEnd] = useState('13:00');
  const [overwrite, setOverwrite] = useState(false);

  const inp = {
    width: '100%', padding: '7px 10px', border: '1px solid #d1d5db',
    borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box',
  };

  const toggleDay = d => {
    setWeekdays(weekdays.includes(d) ? weekdays.filter(x => x !== d) : [...weekdays, d]);
  };

  const apply = () => {
    if (fromDay > toDay) { alert('시작일이 종료일보다 큼'); return; }
    if (!weekdays.length) { alert('최소 1개 요일 선택'); return; }
    onApply({
      fromDay, toDay, weekdays,
      startTime, endTime,
      breakStart: hasBreak ? breakStart : null,
      breakEnd: hasBreak ? breakEnd : null,
      overwrite,
    });
  };

  return (
    <div onClick={onCancel}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1100,
               display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px',
               overflow: 'auto' }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: '#fff', borderRadius: '12px', padding: '20px',
                 maxWidth: '420px', width: '100%', maxHeight: '90vh', overflow: 'auto' }}>
        <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#18181B', margin: '0 0 4px' }}>
          📋 일괄 입력
        </h3>
        <p style={{ fontSize: '11px', color: '#9ca3af', margin: '0 0 14px' }}>
          {empName} · {year}년 {month}월
        </p>

        {/* 기간 */}
        <Field label="기간 (이번 달 안)">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: '6px', alignItems: 'center' }}>
            <input type="number" min="1" max={days} value={fromDay}
              onChange={e => setFromDay(Math.max(1, Math.min(days, Number(e.target.value) || 1)))} style={inp} />
            <span style={{ fontSize: '13px', color: '#6b7280' }}>~</span>
            <input type="number" min="1" max={days} value={toDay}
              onChange={e => setToDay(Math.max(1, Math.min(days, Number(e.target.value) || days)))} style={inp} />
          </div>
        </Field>

        {/* 요일 */}
        <Field label="적용 요일">
          <div style={{ display: 'flex', gap: '4px' }}>
            {KOR_DAYS.map((d, i) => (
              <button key={i} onClick={() => toggleDay(i)} type="button"
                style={{
                  flex: 1, padding: '8px 0', borderRadius: '6px',
                  border: weekdays.includes(i) ? 'none' : '1px solid #e5e7eb',
                  background: weekdays.includes(i)
                    ? (i === 0 ? '#52525B' : i === 6 ? '#2563eb' : '#FF6F0F')
                    : '#fff',
                  color: weekdays.includes(i) ? '#fff' : '#9ca3af',
                  fontSize: '13px', fontWeight: '700', cursor: 'pointer',
                }}>{d}</button>
            ))}
          </div>
        </Field>

        {/* 근무 시간 */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
          <Field label="근무 시작">
            <input type="time" value={startTime} onChange={e => setStartTime(e.target.value)} style={inp} />
          </Field>
          <Field label="근무 종료">
            <input type="time" value={endTime} onChange={e => setEndTime(e.target.value)} style={inp} />
          </Field>
        </div>

        {/* 휴식 */}
        <Field label="">
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px',
                          padding: '6px', background: '#FAFAFA', borderRadius: '6px', cursor: 'pointer' }}>
            <input type="checkbox" checked={hasBreak} onChange={e => setHasBreak(e.target.checked)} />
            <span>☕ 휴식 시간 추가</span>
          </label>
        </Field>
        {hasBreak && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <Field label="휴식 시작">
              <input type="time" value={breakStart} onChange={e => setBreakStart(e.target.value)} style={inp} />
            </Field>
            <Field label="휴식 종료">
              <input type="time" value={breakEnd} onChange={e => setBreakEnd(e.target.value)} style={inp} />
            </Field>
          </div>
        )}

        {/* 덮어쓰기 옵션 */}
        <Field label="">
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px',
                          padding: '6px', background: '#fef2f2', borderRadius: '6px', cursor: 'pointer' }}>
            <input type="checkbox" checked={overwrite} onChange={e => setOverwrite(e.target.checked)} />
            <span><strong>기존 입력 덮어쓰기</strong> (체크 해제 시 빈 날만)</span>
          </label>
        </Field>

        <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end', marginTop: '14px' }}>
          <button onClick={onCancel}
            style={{ padding: '8px 14px', borderRadius: '6px', border: '1px solid #e5e7eb',
                     background: '#fff', cursor: 'pointer', fontSize: '13px' }}>취소</button>
          <button onClick={apply}
            style={{ padding: '8px 18px', borderRadius: '6px', border: 'none',
                     background: '#FF6F0F', color: '#fff', fontSize: '13px',
                     fontWeight: '700', cursor: 'pointer' }}>적용</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: '12px' }}>
      {label && (
        <div style={{ fontSize: '12px', fontWeight: '700', color: '#6b7280', marginBottom: '4px' }}>{label}</div>
      )}
      {children}
    </div>
  );
}
